import HeaderHealthSubItems from '@Components/Home/HeaderHealthSubItems';
import { TAside, TFooter } from '@models/header.model';
import { Products } from '@tameeni/shared-logic';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useSearchParams, useLocation } from 'react-router-dom';
import { getEnv } from 'src/utils/envHelper';

export function useHomeLinks() {
  const {
    t,
    i18n: { language: locale },
  } = useTranslation([
    'routes',
    'header',
    'footer',
    'domestic-header',
    'domestic-footer',
    'mmp-header',
    'mmp-footer',
    'healthSmeHome',
    'psHome',
    'fleet-home',
    'products',
  ]);
  const { pathname } = useLocation();

  const { user } = useAuth();
  const id = user?.profile?.IdentityNumber;
  const [searchParams] = useSearchParams();

  // Helper function to build query string
  const buildQueryString = (baseUrl: string) => {
    const queryString = searchParams.toString();
    const hasParams = baseUrl.includes('?');
    if (!queryString) return baseUrl;
    return `${baseUrl}${hasParams ? '&' : '?'}${queryString}`;
  };

  const PRODUCT_PATHS = {
    [Products.CAR]: ['/', '/en'],
    [Products.MEDICAL_MALPRACTICE]: ['/mmp', '/en/mmp'],
    [Products.TRAVEL]: ['/travel', '/en/travel'],
    [Products.HEALTH_SME]: ['/health-sme', '/en/health-sme'],
    [Products.HEALTH_INDIVIDUAL]: ['/health', '/en/health'],
    [Products.PROTECTION_AND_SAVING]: ['/ps', '/en/ps'],
    [Products.FLEET_SME]: ['/fleet', '/en/fleet'],
    [Products.HOME]: ['/home-insurance', '/en/home-insurance'],
    [Products.PREMIUM_RESIDENCY]: ['/health-pr', '/en/health-pr'],
  };

  const HOME_LINKS = {
    [Products.CAR]: t('routes:routes.home'),
    [Products.HEALTH_INDIVIDUAL]: t('routes:routes.health-individual'),
    [Products.HEALTH_SME]: t('routes:routes.health-sme'),
    [Products.TRAVEL]: t('routes:routes.travel-home'),
    [Products.MEDICAL_MALPRACTICE]: t('routes:routes.mmp-home'),
    [Products.PROTECTION_AND_SAVING]: t('routes:routes.ps-home'),
    [Products.FLEET_SME]: t('routes:routes.fleet-home'),
    [Products.HOME]: t('routes:routes.home-insurance'),
    [Products.MARINE]: t('routes:routes.marine-home'),
    [Products.PREMIUM_RESIDENCY]: t('routes:routes.premium-residency'),
  };

  const ICONS = {
    [Products.CAR]: 'MotorColored',
    [Products.HEALTH_INDIVIDUAL]: 'HealthColored',
    [Products.HEALTH_SME]: 'HealthColored',
    [Products.TRAVEL]: 'TravelColored',
    [Products.MEDICAL_MALPRACTICE]: 'MMPColored',
    [Products.PROTECTION_AND_SAVING]: 'LifeColored',
    [Products.FLEET_SME]: 'FleetColored',
    [Products.HOME]: 'HomeColored',
    [Products.MARINE]: 'MarineColored',
    [Products.PREMIUM_RESIDENCY]: 'LifeColored',
  };

  const GREY_ICONS = {
    [Products.CAR]: 'Motor',
    [Products.HEALTH_INDIVIDUAL]: 'Health',
    [Products.HEALTH_SME]: 'Health',
    [Products.TRAVEL]: 'Travel',
    [Products.MEDICAL_MALPRACTICE]: 'MMP',
    [Products.PROTECTION_AND_SAVING]: 'Life',
    [Products.FLEET_SME]: 'Fleet',
    [Products.HOME]: 'Home',
    [Products.MARINE]: 'Marine',
    [Products.PREMIUM_RESIDENCY]: 'Life',
  };

  const PRODUCTS_TO_SHOW = ['preprod', 'prod'].includes(getEnv()) //CHECK PRODUCTS FLAT FOR ALL PRODUCTS
    ? [
        Products.CAR,
        Products.HEALTH_SME,
        Products.MEDICAL_MALPRACTICE,
        Products.TRAVEL,
        Products.PROTECTION_AND_SAVING,
        Products.FLEET_SME,
        Products.HOME,
      ]
    : [
        Products.CAR,
        Products.HEALTH_SME,
        Products.MEDICAL_MALPRACTICE,
        Products.TRAVEL,
        Products.PROTECTION_AND_SAVING,
        Products.FLEET_SME,
        Products.HOME,
      ];

  const INDIVIDUAL_PRODUCTS = ['preprod', 'prod'].includes(getEnv())
    ? [
        Products.CAR,
        Products.MEDICAL_MALPRACTICE,
        Products.HEALTH_INDIVIDUAL,
        Products.TRAVEL,
        Products.PROTECTION_AND_SAVING,
        Products.HOME,
        Products.PREMIUM_RESIDENCY,
      ]
    : [
        Products.CAR,
        Products.MEDICAL_MALPRACTICE,
        Products.HEALTH_INDIVIDUAL,
        Products.TRAVEL,
        Products.PROTECTION_AND_SAVING,
        Products.HOME,
        Products.PREMIUM_RESIDENCY,
      ];

  const SME_PRODUCTS = ['preprod', 'prod'].includes(getEnv())
    ? [Products.HEALTH_SME, Products.FLEET_SME]
    : [Products.HEALTH_SME, Products.FLEET_SME];

  const PRODUCTS_FLAT = ['preprod', 'prod'].includes(getEnv())
    ? [
        Products.CAR,
        Products.HEALTH_SME,
        Products.MEDICAL_MALPRACTICE,
        Products.HEALTH_INDIVIDUAL,
        Products.TRAVEL,
        Products.PROTECTION_AND_SAVING,
        Products.PREMIUM_RESIDENCY,
        Products.FLEET_SME,
        Products.HOME,
      ]
    : [
        Products.CAR,
        Products.HEALTH_SME,
        Products.MEDICAL_MALPRACTICE,
        Products.HEALTH_INDIVIDUAL,
        Products.TRAVEL,
        Products.PROTECTION_AND_SAVING,
        Products.FLEET_SME,
        Products.HOME,
        Products.PREMIUM_RESIDENCY,
      ];

  const STEP_ONE_LINKS = {
    [Products.CAR]: buildQueryString(`/UHome/PurchaseFlow?appflow=renewal${id ? `&ph=${id}` : ''}`),
    [Products.HEALTH_INDIVIDUAL]: buildQueryString(
      `${window.settings.HEALTH_INDIVIDUAL_ORIGIN}/${locale}`,
    ),
    [Products.HEALTH_SME]: buildQueryString(`${window.settings.VITE_HEALTH_SME_ORIGIN}/${locale}`),
    [Products.TRAVEL]: buildQueryString(
      `${window.settings.TRAVEL_ORIGIN}/${locale}/travel-journey?step=1`,
    ),
    [Products.MEDICAL_MALPRACTICE]: buildQueryString(`${window.settings.MMP_ORIGIN}/${locale}`),
    [Products.PROTECTION_AND_SAVING]: buildQueryString(`${window.settings.PS_ORIGIN}/${locale}`),
    [Products.FLEET_SME]: buildQueryString(
      `${window.settings.FLEET_SME_ORIGIN}/${locale}/sme-application/company-details/basic-details`,
    ),
    [Products.HOME]: buildQueryString(
      `${window.settings.HOME_INSURANCE_ORIGIN}/${locale}/quote-basic-data`,
    ),
    [Products.PREMIUM_RESIDENCY]: buildQueryString(
      `${window.settings.PREMIUM_RESIDENCY_ORIGIN}/${locale}`,
    ),
  };

  const motorSupport = (t('header:aside', { returnObjects: true }) as TAside)?.support.map(
    (obj) => ({
      ...obj,
      icon: 'LinkArrow',
      iconClassName: 'rtl:scale-x-[-1]',
    }),
  );

  const healthSMEHeaderSupport = (
    t('healthSmeHome:header', { returnObjects: true }) as TAside
  )?.support.map((obj) => ({
    ...obj,
    link: `${window.settings.VITE_HEALTH_SME_ORIGIN}${obj.link}`,
    icon: 'LinkArrow',
    iconClassName: 'rtl:scale-x-[-1]',
  }));

  const domesticHeaderSupport = (
    t('domestic-header:aside', { returnObjects: true }) as TAside
  )?.support.map((obj) => ({
    ...obj,
    icon: 'LinkArrow',
    iconClassName: 'rtl:scale-x-[-1]',
  }));

  const psHeaderSupport = (t('psHome:header', { returnObjects: true }) as TAside)?.support.map(
    (obj) => ({
      ...obj,
      icon: 'LinkArrow',
      iconClassName: 'rtl:scale-x-[-1]',
    }),
  );

  const fleetHeaderSupport = (
    t('healthSmeHome:header', { returnObjects: true }) as TAside
  )?.support.map((obj) => ({
    ...obj,
    link: `${window.settings.FLEET_SME_ORIGIN}${obj.link}`,
    icon: 'LinkArrow',
    iconClassName: 'rtl:scale-x-[-1]',
  }));

  const HEADER_SUPPORT_LINKS: Partial<
    Record<Products, { label: string; link: string; icon: string }[]>
  > = {
    [Products.CAR]: motorSupport,
    [Products.HEALTH_INDIVIDUAL]: domesticHeaderSupport,
    [Products.HEALTH_SME]: healthSMEHeaderSupport,
    [Products.TRAVEL]: domesticHeaderSupport,
    [Products.MEDICAL_MALPRACTICE]: domesticHeaderSupport,
    [Products.PROTECTION_AND_SAVING]: psHeaderSupport,
    [Products.FLEET_SME]: fleetHeaderSupport,
  };

  const motorHeaderResources = (
    t('header:aside', { returnObjects: true }) as TAside
  )?.resources.map((obj) => ({
    ...obj,
    icon: 'LinkArrow',
    iconClassName: 'rtl:scale-x-[-1]',
  }));

  const domesticHeaderResources = (
    t('domestic-header:aside', { returnObjects: true }) as TAside
  )?.resources.map((obj) => ({
    ...obj,
    icon: 'LinkArrow',
    iconClassName: 'rtl:scale-x-[-1]',
  }));

  const healthSMEHeaderResources = (
    t('healthSmeHome:header', { returnObjects: true }) as TAside
  )?.resources.map((obj) => ({
    ...obj,
    link: `${window.settings.VITE_HEALTH_SME_ORIGIN}${obj.link}`,
    icon: 'LinkArrow',
    iconClassName: 'rtl:scale-x-[-1]',
  }));

  const mmpHeaderResources = (
    t('mmp-header:aside', { returnObjects: true }) as TAside
  )?.resources.map((obj) => ({
    ...obj,
    icon: 'LinkArrow',
    link: `${window.settings.MMP_ORIGIN}${obj.link}`,
    iconClassName: 'rtl:scale-x-[-1]',
  }));

  const psHeaderResources = (t('psHome:header', { returnObjects: true }) as TAside)?.resources.map(
    (obj) => ({
      ...obj,
      icon: 'LinkArrow',
      link: `${window.settings.PS_ORIGIN}${obj.link}`,
      iconClassName: 'rtl:scale-x-[-1]',
    }),
  );

  const fleetHeaderResources = (
    t('fleet-home:header', { returnObjects: true }) as TAside
  )?.resources.map((obj) => ({
    ...obj,
    icon: 'LinkArrow',
    link: `${window.settings.FLEET_SME_ORIGIN}${obj.link}`,
    iconClassName: 'rtl:scale-x-[-1]',
  }));

  const HEADER_RESOURCES_LINKS: Partial<
    Record<Products, { label: string; link: string; icon: string }[]>
  > = {
    [Products.CAR]: motorHeaderResources,
    [Products.HEALTH_INDIVIDUAL]: domesticHeaderResources,
    [Products.HEALTH_SME]: healthSMEHeaderResources,
    [Products.TRAVEL]: domesticHeaderResources,
    [Products.MEDICAL_MALPRACTICE]: mmpHeaderResources,
    [Products.PROTECTION_AND_SAVING]: psHeaderResources,
    [Products.FLEET_SME]: fleetHeaderResources,
  };

  const motorFooterResources = (
    t('footer:footer', { returnObjects: true }) as TFooter
  )?.resources.map((obj) => ({
    ...obj,
  }));

  const domesticFooterResources = (
    t('domestic-footer:footer', { returnObjects: true }) as TFooter
  )?.resources.map((obj) => ({
    ...obj,
  }));

  const mmpFooterResources = (
    t('mmp-footer:footer', { returnObjects: true }) as TFooter
  )?.resources.map((obj) => ({
    ...obj,
    url: `${window.settings.MMP_ORIGIN}${obj.url}`,
  }));

  const healthSMEFooterResources = (
    t('healthSmeHome:footer', { returnObjects: true }) as TFooter
  )?.resources.map((obj) => ({
    ...obj,
    url: `${window.settings.VITE_HEALTH_SME_ORIGIN}${obj.url}`,
  }));

  const psFooterResources = (t('psHome:footer', { returnObjects: true }) as TFooter)?.resources.map(
    (obj) => ({
      ...obj,
      url: `${window.settings.PS_ORIGIN}${obj.url}`,
    }),
  );

  const fleetFooterResources = (
    t('fleet-home:footer', { returnObjects: true }) as TFooter
  )?.resources.map((obj) => ({
    ...obj,
    url: `${window.settings.FLEET_SME_ORIGIN}${obj.url}`,
  }));

  const FOOTER_RESOURCES_LINKS: Partial<Record<Products, { label: string; url: string }[]>> = {
    [Products.CAR]: motorFooterResources,
    [Products.HEALTH_INDIVIDUAL]: domesticFooterResources,
    [Products.HEALTH_SME]: healthSMEFooterResources,
    [Products.TRAVEL]: domesticFooterResources,
    [Products.MEDICAL_MALPRACTICE]: mmpFooterResources,
    [Products.PROTECTION_AND_SAVING]: psFooterResources,
    [Products.FLEET_SME]: fleetFooterResources,
    [Products.HOME]: motorFooterResources,
    [Products.PREMIUM_RESIDENCY]: motorFooterResources,
  };

  const motorFooterSupport = (t('footer:footer', { returnObjects: true }) as TFooter)?.support.map(
    (obj) => ({
      ...obj,
    }),
  );

  const domesticFooterSupport = (
    t('domestic-footer:footer', { returnObjects: true }) as TFooter
  )?.support.map((obj) => ({
    ...obj,
  }));

  const healthSMEFooterSupport = (
    t('healthSmeHome:footer', { returnObjects: true }) as TFooter
  )?.support.map((obj) => ({
    ...obj,
    url: `${window.settings.VITE_HEALTH_SME_ORIGIN}${obj.url}`,
  }));

  const psFooterSupport = (t('psHome:footer', { returnObjects: true }) as TFooter)?.support.map(
    (obj) => ({
      ...obj,
      url: `${window.settings.PS_ORIGIN}${obj.url}`,
    }),
  );

  const fleetFooterSupport = (
    t('fleet-home:footer', { returnObjects: true }) as TFooter
  )?.support.map((obj) => ({
    ...obj,
    url: `${window.settings.FLEET_SME_ORIGIN}${obj.url}`,
  }));

  const FOOTER_SUPPORT_LINKS: Partial<Record<Products, { label: string; url: string }[]>> = {
    [Products.CAR]: motorFooterSupport,
    [Products.HEALTH_INDIVIDUAL]: domesticFooterSupport,
    [Products.HEALTH_SME]: healthSMEFooterSupport,
    [Products.TRAVEL]: domesticFooterSupport,
    [Products.MEDICAL_MALPRACTICE]: domesticFooterSupport,
    [Products.PROTECTION_AND_SAVING]: psFooterSupport,
    [Products.FLEET_SME]: fleetFooterSupport,
    [Products.HOME]: motorFooterSupport,
    [Products.PREMIUM_RESIDENCY]: motorFooterSupport,
  };

  const STORE_LINKS = {
    appStore:
      'https://apps.apple.com/us/app/tameeni-%D8%AA%D8%A3%D9%85%D9%8A%D9%86%D9%8A/id1501339487',
    googlePlay: 'https://play.google.com/store/apps/details?id=tameeni.com&pcampaignid=web_share',
    appGallery: 'https://appgallery.huawei.com/app/C104656303',
  };

  const PRODUCTS = Object.keys(Products)
    .map((product) => {
      if (!PRODUCTS_TO_SHOW.includes(Products[product as keyof typeof Products] as Products)) {
        return undefined;
      }
      if (product === 'HEALTH_SME') {
        return {
          label: t('products:HEALTH_INSURANCE'),
          link: HOME_LINKS[Products[product as keyof typeof Products] as Products],
          icon: ICONS[Products[product as keyof typeof Products] as Products],
          subItems: HeaderHealthSubItems({
            HOME_LINKS,
            className: 'mx-auto flex flex-col gap-4 w-full max-w-7xl',
            PRODUCTS_FLAT,
          }),
        };
      }
      return {
        label: t(`products:${product}`),
        link: HOME_LINKS[Products[product as keyof typeof Products] as Products],
        icon: ICONS[Products[product as keyof typeof Products] as Products],
      };
    })
    .filter((product) => product !== undefined);

  const SOCIAL_LINKS = [
    {
      label: 'X',
      url: 'https://twitter.com/TameeniKSA',
      icon: 'XLogo',
    },
    {
      label: 'Facebook',
      url: 'https://www.facebook.com/Tameeniksa/',
      icon: 'Facebook',
    },
    {
      label: 'Instagram',
      url: 'https://www.instagram.com/tameeniksa',
      icon: 'Instagram',
    },
    {
      label: 'Snapchat',
      url: 'https://www.snapchat.com/add/tameeniksa',
      icon: 'Snapchat',
    },
    {
      label: 'Youtube',
      url: 'https://www.youtube.com/channel/UCRLnHZkwAWJW0UN5CvA6Xsg/',
      icon: 'Youtube',
    },
  ];

  const getActiveProduct = () => {
    for (const [product, paths] of Object.entries(PRODUCT_PATHS)) {
      if (paths.includes(pathname)) {
        return product as Products;
      }
    }
    return Products.CAR;
  };

  const getActiveProductMega = () => {
    for (const [product, paths] of Object.entries(PRODUCT_PATHS)) {
      if (
        (product === Products.HEALTH_SME ||
          product === Products.HEALTH_INDIVIDUAL ||
          product === Products.PREMIUM_RESIDENCY) &&
        paths.includes(pathname)
      ) {
        return Products.HEALTH_SME;
      } else if (paths.includes(pathname)) {
        return product as Products;
      }
    }
    return Products.CAR;
  };

  const NEW_PRODUCTS = ['preprod', 'prod'].includes(getEnv())
    ? [
        Products.PROTECTION_AND_SAVING,
        Products.HOME,
        Products.FLEET_SME,
        Products.PREMIUM_RESIDENCY,
      ]
    : [
        Products.PROTECTION_AND_SAVING,
        Products.HOME,
        Products.FLEET_SME,
        Products.PREMIUM_RESIDENCY,
      ];

  return {
    HOME_LINKS,
    STEP_ONE_LINKS,
    HEADER_SUPPORT_LINKS,
    HEADER_RESOURCES_LINKS,
    FOOTER_RESOURCES_LINKS,
    FOOTER_SUPPORT_LINKS,
    PRODUCTS_TO_SHOW,
    INDIVIDUAL_PRODUCTS,
    SME_PRODUCTS,
    STORE_LINKS,
    PRODUCTS,
    SOCIAL_LINKS,
    ICONS,
    PRODUCT_PATHS,
    getActiveProduct,
    GREY_ICONS,
    NEW_PRODUCTS,
    PRODUCTS_FLAT,
    getActiveProductMega,
  };
}
